import React, { useState, useEffect } from 'react';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './App.css';
import './index.css';
import Titlebar from './Titlebar';
import CustomerComboBox from './CustomerComboBox';
import { apiUrl } from './config';

function SpotAddForm() {
  const navigate = useNavigate();
  const { spotId } = useParams(); // Get spot ID from URL for editing
  const isEditMode = Boolean(spotId);
  
  const [customers, setCustomers] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerComboBoxKey, setCustomerComboBoxKey] = useState(0);
  const [isLoading, setIsLoading] = useState(isEditMode);
  
  const [formData, setFormData] = useState({
    cart: '',
    cut: '',
    isci_code: '',
    client_name: '', 
    spot_title: '',
    description: '',
    start_date: '',
    thru_date: '',
    vocal_style: '',
    local_cache_dir: '',
    copy_link: '',
    file_link: '',
    run_length: '',
    audio_file: '',
  });
  
  const [audioFile, setAudioFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null);
  const [isThruDateTbd, setIsThruDateTbd] = useState(false);

// Fetch spot data for editing - wrapped in useCallback
  const fetchSpot = useCallback(async (spotId) => {
    try {
      setIsLoading(true);
      const response = await fetch(apiUrl(`/media/one/${spotId}`));
      
      if (!response.ok) {
        throw new Error(`Failed to fetch spot: ${response.status}`);
      }
      
      const spot = await response.json();
      console.log('Fetched spot for editing:', spot);
      
      // Update form data with spot data
      const loadedThruDate = spot.thru_date || spot.end_date || '';
      setFormData({
        cart: spot.cart || '',
        cut: spot.cut || '',
        isci_code: spot.isci_code || '',
        client_name: spot.client_name || '',
        spot_title: spot.spot_title || '',
        description: spot.description || '',
        start_date: spot.start_date || '',
        thru_date: loadedThruDate,
        vocal_style: spot.vocal_style || '',
        local_cache_dir: spot.local_cache_dir || '',
        copy_link: spot.copy_link || '',
        file_link: spot.file_link || '',
        run_length: spot.run_length || '',
        audio_file: spot.audio_file || '',
      });
      setIsThruDateTbd(!loadedThruDate);
      
      // Set uploaded file URL if exists
      if (spot.audio_file || spot.file_link) {
        setUploadedFileUrl(spot.audio_file || spot.file_link);
      }
      
      // Find and set the customer if customer_id exists
      if (spot.customer_id && customers) {
        const customer = customers.find(c => c._id === spot.customer_id || c.id === spot.customer_id);
        if (customer) {
          setSelectedCustomer(customer);
          setCustomerComboBoxKey(prev => prev + 1); // Force re-render of combo box
        }
      }
      
    } catch (error) {
      console.error('Error fetching spot:', error);
      alert(`Failed to load spot: ${error.message}`);
      navigate('/spots');
    } finally {
      setIsLoading(false);
    }
  }, [customers, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'thru_date' && value) {
      setIsThruDateTbd(false);
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleThruDateTbdChange = (event) => {
    const checked = event.target.checked;
    setIsThruDateTbd(checked);
    if (checked) {
      setFormData((prev) => ({
        ...prev,
        thru_date: '',
      }));
    }
  };

  const handleCustomerChange = (customer) => {
    console.log('Selected customer:', customer);
    setSelectedCustomer(customer);
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type.startsWith('audio/') || file.name.endsWith('.mp3') || file.name.endsWith('.wav') || file.name.endsWith('.m4a'))) {
      setAudioFile(file);
      setUploadedFileUrl(null); // Clear uploaded URL until actual upload succeeds
    } else {
      alert('Please select a valid audio file (MP3, WAV, or M4A)');
      e.target.value = '';
    }
  };

  const uploadAudioFile = async (fileToUpload) => {
    if (!fileToUpload) {
      alert('Please select an audio file first');
      return null;
    }

    // Validate cart is available
    if (!formData.cart) {
      alert('Please enter a cart number before uploading');
      return null;
    }

    setIsUploadingFile(true);
    
    try {
      // Extract file extension
      const fileName = fileToUpload.name;
      const fileExtension = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
      
      // Validate extension
      if (!['mp3', 'wav', 'm4a'].includes(fileExtension)) {
        alert('Invalid file extension. Please upload MP3, WAV, or M4A files only.');
        return null;
      }
      
      // Construct new filename: cart (padded to 6 digits) + cut suffix (2 digits) + extension
      const cartPadded = formData.cart.padStart(6, '0');
      const cutPadded = String(formData.cut || '001').padStart(3, '0').slice(-3);
      const cutTrimmed = cutPadded.substring(1);
      const newFileName = `${cartPadded}${cutTrimmed}.${fileExtension}`;
      
      const uploadData = new FormData();
      uploadData.append('audio_file', fileToUpload, newFileName);
      
      const response = await fetch(apiUrl('/media/upload'), {
        method: 'POST',
        body: uploadData,
      });

      if (response.ok) {
        let result = {};
        try {
          result = await response.json();
        } catch (parseError) {
          console.warn('Upload response had no JSON body, using filename fallback URL:', parseError);
        }

        const uploadedUrl = result.file_url || result.url || result.audio_file || apiUrl(`/audio/${newFileName}`);
        setUploadedFileUrl(uploadedUrl);
        setAudioFile(null);
        alert('Audio file uploaded successfully');
        console.log('File uploaded:', result);
        return uploadedUrl;
      } else {
        const errorText = await response.text();
        console.error('Upload failed:', errorText);
        alert(`Failed to upload file: ${response.status} ${response.statusText}`);
        return null;
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file');
      return null;
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleFileUpload = async () => {
    await uploadAudioFile(audioFile);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.start_date && formData.thru_date && formData.thru_date < formData.start_date) {
      alert('Thru Date cannot be earlier than Start Date');
      return;
    }
    
    // Basic validation
    if (!formData.cart || !formData.description) {
      alert('Cart number and description are required');
      return;
    }

    setIsUploading(true);
    
    try {
      console.log(`${isEditMode ? 'Updating' : 'Creating'} spot data`);

      // Prepare JSON data for submission
      const submissionData = { ...formData };

      if (isThruDateTbd) {
        submissionData.thru_date = '';
      }
      
      // Include customer ID if selected
      if (selectedCustomer) {
        submissionData.customer_id = selectedCustomer._id || selectedCustomer.id;
        submissionData.customer_name = selectedCustomer.full_name || selectedCustomer.name;
      }
      
      // Normalize cut for persistence (keep as 3 digits for backend storage)
      submissionData.cut = String(submissionData.cut || '001').padStart(3, '0').slice(-3);

      let finalUploadedFileUrl = uploadedFileUrl;

      // If a new file is selected but not uploaded yet, upload it during submit
      if (audioFile && !finalUploadedFileUrl) {
        finalUploadedFileUrl = await uploadAudioFile(audioFile);
        if (!finalUploadedFileUrl) {
          alert('Audio upload failed. Please try again.');
          setIsUploading(false);
          return;
        }
      }

      // Include uploaded file URL if available
      if (finalUploadedFileUrl) {
        submissionData.audio_file = finalUploadedFileUrl;
        // Extract extension from uploadedFileUrl, fallback to mp3
        const urlParts = finalUploadedFileUrl.split('.');
        const fileExt = urlParts.length > 1 ? urlParts[urlParts.length - 1].toLowerCase() : 'mp3';
        // Format: cart (padded 6) + cut (2 digits, no dash) + extension
        const cartPadded = submissionData.cart.padStart(6, '0');
        const cutTrimmed = submissionData.cut.substring(1);
        submissionData.file_link = apiUrl(`/audio/${cartPadded}${cutTrimmed}.${fileExt}`);
      }

      console.log('Final submission data:', submissionData);

      // Choose endpoint and method based on mode
      const url = isEditMode 
        ? apiUrl(`/media/update/${spotId}`)
        : apiUrl('/media/create');
      
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      if (response.ok) {
        alert(`Spot ${isEditMode ? 'updated' : 'created'} successfully`);
        navigate('/spots'); // Navigate back to spots list
      } else {
        const errorText = await response.text();
        console.error('Server response:', errorText);
        alert(`Failed to ${isEditMode ? 'update' : 'create'} spot: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} spot:`, error);
      alert(`Error ${isEditMode ? 'updating' : 'creating'} spot`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleGenerateSpec = async () => {
    if (!formData.description) {
      alert('Description is required to generate a spec spot');
      return;
    }

    setIsGenerating(true);
    
    try {
      const response = await fetch(apiUrl('/spot/generate-spec'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          description: formData.description,
          vocal_style: formData.vocal_style,
          run_length: formData.run_length
        }),
      });

      if (response.ok) {
        alert('Spec spot generation started');
        // Optionally update form with generated data
      } else {
        alert('Failed to generate spec spot');
      }
    } catch (error) {
      console.error('Error generating spec spot:', error);
      alert('Error generating spec spot');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCancel = () => {
    navigate('/spots');
  };

  // Fetch customers on component mount
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await fetch(apiUrl(`/customer/list`));
        const customers = await response.json();
        setCustomers(customers);
        console.log('Customers loaded:', customers);
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    };

    fetchCustomers();
  }, []);

  // Fetch spot data when in edit mode and customers are loaded
  useEffect(() => {
    if (isEditMode && customers && customers.length > 0) {
      fetchSpot(spotId); // Use spotId from params
    }
  }, [isEditMode, spotId, customers, fetchSpot]);

  // Loading state for edit mode
  if (isEditMode && isLoading) {
    return (
      <div className="lg:pl-72 sm:ml-0 px-0 sm:px-0">
        <Titlebar title="Edit Spot"/>
        <div className="m-3">
          <div className="text-center py-12">
            <div className="text-gray-500">Loading spot data...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:pl-72 sm:ml-0 px-0 sm:px-0">
      <Titlebar title={isEditMode ? "Edit Spot" : "Add New Spot"}/>
      
      <div className="m-3">

        <form onSubmit={handleSubmit}>
          <div className="space-y-12">
            {/* Spot Information Section */}
            <div className="border-b border-gray-900/10 pb-12">
              <h2 className="text-base font-semibold leading-7 text-gray-900">Spot Information</h2>
              <p className="mt-1 text-sm leading-6 text-gray-600">
                {isEditMode ? "Update the details for this spot." : "Enter the details for the new spot."}
              </p>

              <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                {/* Cart Number */}
                <div className="sm:col-span-2">
                  <label htmlFor="cart" className="block text-sm font-medium leading-6 text-gray-900">
                    Cart Number *
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="cart"
                      id="cart"
                      value={formData.cart}
                      onChange={handleInputChange}
                      required
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      placeholder="90246"
                    />
                  </div>
                </div>

                {/* Cut */}
                <div className="sm:col-span-2">
                  <label htmlFor="cut" className="block text-sm font-medium leading-6 text-gray-900">
                    Cut
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="cut"
                      id="cut"
                      value={formData.cut}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      placeholder="001"
                    />
                  </div>
                </div>

                {/* ISCI Code */}
                <div className="sm:col-span-2">
                  <label htmlFor="isci_code" className="block text-sm font-medium leading-6 text-gray-900">
                    ISCI Code
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="isci_code"
                      id="isci_code"
                      value={formData.isci_code}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      placeholder="ABCD1234H30"
                    />
                  </div>
                </div>

                {/* Customer */}
                <div className="sm:col-span-2">
                  <div className="w-full pt-0 sm:ml-0 sm:mt-0 sm:flex-none">
                    <CustomerComboBox 
                      key={`customer-${customerComboBoxKey}-${selectedCustomer ? selectedCustomer._id : 'no-customer'}`}
                      add={true} 
                      addressBlock={false}
                      people={customers} 
                      value={selectedCustomer}
                      onChange={handleCustomerChange}
                    />
                  </div>
                </div>

                {/* Spot Title */}
                <div className="sm:col-span-2">
                  <label htmlFor="spot_title" className="block text-sm font-medium leading-6 text-gray-900">
                    Spot Title
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="spot_title"
                      id="spot_title"
                      value={formData.spot_title}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      placeholder="Enter spot title..."
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="sm:col-span-6">
                  <label htmlFor="description" className="block text-sm font-medium leading-6 text-gray-900">
                    Description *
                  </label>
                  <div className="mt-2">
                    <textarea
                      name="description"
                      id="description"
                      rows={3}
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      placeholder="Enter spot description..."
                    />
                  </div>
                </div>

                {/* Start Date */}
                <div className="sm:col-span-3">
                  <label htmlFor="start_date" className="block text-sm font-medium leading-6 text-gray-900">
                    Start Date
                  </label>
                  <div className="mt-2">
                    <input
                      type="date"
                      name="start_date"
                      id="start_date"
                      value={formData.start_date}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                {/* Thru Date */}
                <div className="sm:col-span-3">
                  <div className="flex items-center justify-between">
                    <label htmlFor="thru_date" className="block text-sm font-medium leading-6 text-gray-900">
                      Thru Date
                    </label>
                    <label htmlFor="thru_date_tbd" className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input
                        id="thru_date_tbd"
                        name="thru_date_tbd"
                        type="checkbox"
                        checked={isThruDateTbd}
                        onChange={handleThruDateTbdChange}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                      TBD
                    </label>
                  </div>
                  <div className="mt-2">
                    <input
                      type="date"
                      name="thru_date"
                      id="thru_date"
                      value={formData.thru_date}
                      onChange={handleInputChange}
                      disabled={isThruDateTbd}
                      min={formData.start_date || undefined}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                {/* Vocal Style */}
                <div className="sm:col-span-3">
                  <label htmlFor="vocal_style" className="block text-sm font-medium leading-6 text-gray-900">
                    Vocal Style
                  </label>
                  <div className="mt-2">
                    <select
                      name="vocal_style"
                      id="vocal_style"
                      value={formData.vocal_style}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    >
                      <option value="">Select vocal style...</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="child">Child</option>
                      <option value="mature_male">Mature Male</option>
                      <option value="mature_female">Mature Female</option>
                      <option value="announcer">Announcer</option>
                      <option value="conversational">Conversational</option>
                    </select>
                  </div>
                </div>

                {/* Run Length */}
                <div className="sm:col-span-3">
                  <label htmlFor="run_length" className="block text-sm font-medium leading-6 text-gray-900">
                    Run Length (seconds)
                  </label>
                  <div className="mt-2">
                    <select
                      name="run_length"
                      id="run_length"
                      value={formData.run_length}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    >
                      <option value="">Select length...</option>
                      <option value="10">10 seconds</option>
                      <option value="15">15 seconds</option>
                      <option value="30">30 seconds</option>
                      <option value="45">45 seconds</option>
                      <option value="60">60 seconds</option>
                    </select>
                  </div>
                </div>

                {/* Copy Link */}
                <div className="sm:col-span-3">
                  <label htmlFor="copy_link" className="block text-sm font-medium leading-6 text-gray-900">
                    Copy Link
                  </label>
                  <div className="mt-2">
                    <input
                      type="url"
                      name="copy_link"
                      id="copy_link"
                      value={formData.copy_link}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      placeholder="https://example.com/copy"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Audio Upload Section */}
            <div className="border-b border-gray-900/10 pb-12">
              <h2 className="text-base font-semibold leading-7 text-gray-900">Audio Upload</h2>
              <p className="mt-1 text-sm leading-6 text-gray-600">
                {isEditMode ? "Update the audio file for this spot or keep the existing one." : "Upload an audio file for this spot."}
              </p>

              {/* Show existing audio file in edit mode */}
              {isEditMode && uploadedFileUrl && (
                <div className="mt-4 p-4 bg-blue-50 rounded-md">
                  <p className="text-sm text-blue-700">
                    Current audio file: <a href={uploadedFileUrl} target="_blank" rel="noopener noreferrer" className="underline">View/Listen</a>
                  </p>
                </div>
              )}

              <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="col-span-full">
                  <label htmlFor="audio_file" className="block text-sm font-medium leading-6 text-gray-900">
                    Audio File
                  </label>
                  <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
                    <div className="text-center">
                      <svg className="mx-auto h-12 w-12 text-gray-300" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M11.47 2.47a.75.75 0 011.06 0l4.5 4.5a.75.75 0 01-1.06 1.06l-3.22-3.22V16.5a.75.75 0 01-1.5 0V4.81L8.03 8.03a.75.75 0 01-1.06-1.06l4.5-4.5zM3 15.75a.75.75 0 01.75.75v2.25a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5V16.5a.75.75 0 011.5 0v2.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V16.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
                      </svg>
                      <div className="mt-4 flex text-sm leading-6 text-gray-600">
                        <label htmlFor="audio_file" className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500">
                          <span>{isEditMode ? "Upload new audio file" : "Upload audio file"}</span>
                          <input 
                            id="audio_file" 
                            name="audio_file" 
                            type="file" 
                            accept="audio/*,.mp3,.wav,.m4a"
                            onChange={handleFileChange}
                            className="sr-only"
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs leading-5 text-gray-600">MP3, WAV, M4A files only</p>
                      {audioFile && (
                        <p className="mt-2 text-sm text-green-600">
                          Selected: {audioFile.name}
                        </p>
                      )}
                      {isEditMode && !audioFile && uploadedFileUrl && (
                        <p className="mt-2 text-sm text-blue-600">
                          ✓ Existing audio file will be kept
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Upload Button Section */}
                {audioFile && (
                  <div className="col-span-full">
                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={handleFileUpload}
                        disabled={isUploadingFile}
                        className="inline-flex items-center rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isUploadingFile ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <svg className="-ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            Upload New File
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}                
              </div>
            </div>

            {/* Generate Spec Spot Section - Only show in add mode */}
            {!isEditMode && (
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base font-semibold leading-7 text-gray-900">AI Generation</h2>
                <p className="mt-1 text-sm leading-6 text-gray-600">Generate a spec spot using AI based on the description and vocal style.</p>

                <div className="mt-6">
                  <button
                    type="button"
                    onClick={handleGenerateSpec}
                    disabled={isGenerating || !formData.description}
                    className="inline-flex items-center rounded-md bg-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isGenerating ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Generating...
                      </>
                    ) : (
                      'Generate Spec Spot'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex items-center justify-end gap-x-6">
            <button 
              type="button" 
              onClick={handleCancel}
              className="text-sm font-semibold leading-6 text-gray-900 hover:text-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUploading}
              className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditMode ? 'Update Spot' : 'Create Spot'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SpotAddForm;